// SongsPage.jsx
// Location: src/pages/SongsPage.jsx
// Browse and manage song progression with search, filter, and hide/complete actions

import React, { useState } from 'react';
import { ChevronLeft, Search } from 'lucide-react';
import { getActiveSongs } from '../data/DrumData.v2';
import GradientHeading from '../components/GradientHeading';

/**
 * Songs Page Component
 * Displays all songs in a table with search, filters, and actions
 * 
 * @param {Function} onBack - Callback to return to home
 * @param {Array} songs - Array of all songs
 * @param {Function} onMarkComplete - Callback to mark song complete
 * @param {Function} onToggleHidden - Callback to hide/show song
 * @param {Function} onSetCurrent - Callback to set song as current
 */
const SongsPage = ({ 
  onBack, 
  songs, 
  onMarkComplete, 
  onToggleHidden,
  onSetCurrent 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [sortBy, setSortBy] = useState('level');

  // Filter songs based on hidden toggle
  const displaySongs = showHidden ? songs : getActiveSongs(songs);

  // Filter by search query
  const filteredSongs = displaySongs.filter(song =>
    song.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    song.difficulty.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort songs
  const sortedSongs = [...filteredSongs].sort((a, b) => {
    if (sortBy === 'level') return a.level - b.level;
    if (sortBy === 'difficulty') return a.difficulty.localeCompare(b.difficulty);
    return 0;
  });

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'current':
        return 'bg-blue-100 text-blue-700';
      case 'goal':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        Back
      </button>
      
      {/* Page Header */}
      <GradientHeading size="page" className="mb-8">
       Song Library
      </GradientHeading>
      
      {/* Filters Bar */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search Input */}
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title or difficulty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Show Hidden Checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">Show hidden</span>
          </label>
          
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="level">Level ↑</option>
              <option value="difficulty">Difficulty</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Songs Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lvl
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Links
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedSongs.map(song => (
              <tr 
                key={song.id} 
                className={`hover:bg-gray-50 transition-colors ${song.hidden ? 'opacity-50' : ''}`}
              >
                {/* Level Badge */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${getStatusColor(song.status)}`}>
                    {song.level}
                  </div>
                </td>
                
                {/* Song Info */}
                <td className="px-6 py-4">
                  <div className="font-semibold text-gray-800">
                    {song.title}
                  </div>
                  <div className="text-sm text-gray-500">
                    {song.difficulty} • {song.tempo} • {song.status}
                  </div>
                </td>
                
                {/* Links */}
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <a
                      href={song.tutorialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium transition-all"
                    >
                      Tutorial
                    </a>
                    <a
                      href={song.playalongUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition-all"
                    >
                      Play-along
                    </a>
                    <a
                      href={song.sheetMusic}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-medium transition-all"
                    >
                      Sheet
                    </a>
                  </div>
                </td>
                
                {/* Actions */}
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex gap-2 justify-end">
                    {/* Set as Current Button (for non-current, non-completed songs) */}
                    {song.status !== 'current' && song.status !== 'completed' && (
                      <button
                        onClick={() => onSetCurrent(song.id)}
                        className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-medium transition-all"
                      >
                        Set as Current
                      </button>
                    )}
                    
                    {/* Mark Complete Button (only for current song) */}
                    {song.status === 'current' && (
                      <button
                        onClick={() => onMarkComplete(song.id)}
                        className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition-all"
                      >
                        Complete
                      </button>
                    )}
                    
                    {/* Hide/Show Button (not for goal songs) */}
                    {song.status !== 'goal' && (
                      <button
                        onClick={() => onToggleHidden(song.id)}
                        className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs font-medium transition-all"
                      >
                        {song.hidden ? 'Show' : 'Hide'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Empty State */}
      {sortedSongs.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No songs found matching your criteria
        </div>
      )}
    </div>
  );
};

export default SongsPage;